// ./components/QueueScreen/QueueScreen.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { apiFetch } from "../../utils/api";
import TopBar from "./TopBar";
import SettingsModal from "./../Settings";
import socket from "../../socket";

const FACEID_SWITCH_DELAY_MS = 10000;
const FACEID_TOAST_MS = 2500;

/** ---------------------- utils ---------------------- */
function normTicketId(v) {
  return v ? String(v).split("-")[0] : null;
}
function stableKeyFrom(p) {
  if (p?.order_id != null) return String(p.order_id);
  const rootTid = normTicketId(p?.ticket_instance_id);
  return rootTid || null;
}
function findMatchingTicketKey(tickets, p) {
  const preferred = stableKeyFrom(p);
  if (!preferred) return null;
  const rootTid = normTicketId(p?.ticket_instance_id);
  const byOrder = tickets.find(t => t.order_id != null && String(t.order_id) === preferred);
  if (byOrder) return byOrder.ticket_instance_id;
  const byRoot = tickets.find(t => normTicketId(t.ticket_instance_id) === rootTid);
  return byRoot ? byRoot.ticket_instance_id : preferred;
}
function statusOfTicket(t) {
  if (t.servedAt) return "Finished";
  if (t.readyToServe) return "Ready To Serve";
  if ((t.bumpedCount ?? 0) > 0 || t.anyCourseBumped) return "In Progress";
  return "Received";
}
function statusColors(status, isDark) {
  const key = String(status).toLowerCase();
  const map = {
    received: isDark ? "bg-sky-900/40 border-sky-700" : "bg-sky-50 border-sky-200",
    "in progress": isDark ? "bg-amber-600/35 border-amber-400" : "bg-amber-100 border-amber-300",
    "ready to serve": isDark ? "bg-emerald-900/40 border-emerald-700" : "bg-emerald-50 border-emerald-200",
    finished: isDark ? "bg-gray-800 border-gray-600 animate-pulse-slow" : "bg-gray-200 border-gray-300 animate-pulse-slow",
    served: isDark ? "bg-gray-800 border-gray-600 animate-pulse" : "bg-gray-200 border-gray-300 animate-pulse"
  };
  return map[key] || (isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200");
}
function normalizeCourseLabels(raw) {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return normalizeCourseLabels(JSON.parse(raw));
    } catch {
      return {};
    }
  }
  return raw && typeof raw === "object" ? raw : {};
}
function courseLabelFor(courseNum, courseLabels) {
  const oneBased = Number(courseNum) + 1;
  const configured = String(courseLabels?.[String(oneBased)] || "").trim();
  return configured || `Course ${oneBased}`;
}
function normalizeGuestValue(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeGuestValue(parsed);
    } catch {
      return trimmed;
    }
  }
  if (typeof value === "object") {
    const candidates = [value.name, value.full_name, value.fullName, value.display_name, value.displayName, value.member_name, value.memberName, value.guest_name, value.guestName, value.value];
    for (const candidate of candidates) {
      const normalized = normalizeGuestValue(candidate);
      if (normalized) return normalized;
    }
  }
  return String(value).trim() || null;
}
function normalizeHeaderFields(payload = {}) {
  return {
    user_info: normalizeGuestValue(payload.user_info ?? payload.UserInfo ?? payload.guest_name ?? payload.guestName ?? null),
    table_name: payload.table_name ?? payload.GuestTable ?? payload.table ?? null,
    server_name: payload.server_name ?? payload.ServerName ?? payload.server ?? null,
    destination: payload.destination ?? payload.Destination ?? null
  };
}
function sameFaceAction(a, b) {
  if (!a || !b) return false;
  return a.type === b.type && (a.serverName || "") === (b.serverName || "");
}
function normalizeFaceActionQueue(actions = []) {
  const next = [];
  for (let i = 0; i < actions.length; i += 1) {
    const action = actions[i];
    if (!action) continue;
    if (action.type === "clear") {
      const hasLaterServer = actions.slice(i + 1).some(candidate => candidate?.type === "server");
      if (hasLaterServer) continue;
      if (next[next.length - 1]?.type === "clear") continue;
      next.push({
        type: "clear"
      });
      continue;
    }
    if (action.type === "server") {
      const serverName = String(action.serverName || "").trim();
      if (!serverName) continue;
      const normalized = {
        type: "server",
        serverName
      };
      if (sameFaceAction(next[next.length - 1], normalized)) continue;
      next.push(normalized);
    }
  }
  return next;
}
export default function QueueScreen() {
  const {
    sublocationId
  } = useParams();
  const {
    isDark,
    toggleDarkMode
  } = useDarkMode("queueDarkMode");
  const [showSettings, setShowSettings] = useState(false);
  const [tickets, setTickets] = useState([]);
  const servedTimersRef = useRef(new Map());

  // Expo item index: Map<ticketKey, Map<course, Set<item_id>>>
  const itemsIndexRef = useRef(new Map());
  // Bumped index (for cross-outs): Map<ticketKey, Map<course, Set<item_id>>>
  const bumpedIndexRef = useRef(new Map());
  // If EXPO bump-ticket’ed, future items should auto-bump: Map<ticketKey, boolean>
  const allItemsBumpedRef = useRef(new Map());
  const [sortKey, setSortKey] = useState("time");
  const [filterStatuses, setFilterStatuses] = useState(new Set());
  const [filterServer, setFilterServer] = useState("");
  const [filterTable, setFilterTable] = useState("");
  const [courseLabels, setCourseLabels] = useState({});
  const [stationMetaById, setStationMetaById] = useState({});
  const [expoStationId, setExpoStationId] = useState(null);
  const [faceActionQueue, setFaceActionQueue] = useState([]);
  const [activeFaceAction, setActiveFaceAction] = useState(null);
  const [faceActionRemainingMs, setFaceActionRemainingMs] = useState(FACEID_SWITCH_DELAY_MS);
  const [faceToast, setFaceToast] = useState(null);
  const seenOrderReceivedRef = useRef(new Map());
  const activeFaceActionRef = useRef(null);
  const faceToastTimeoutRef = useRef(null);
  const within = (ms, ts) => typeof ts === "number" && Date.now() - ts < ms;
  const servers = useMemo(() => [...new Set(tickets.map(t => t.server_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [tickets]);
  const tables = useMemo(() => [...new Set(tickets.map(t => t.table_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [tickets]);
  function toggleStatus(s) {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }
  function clearFilters() {
    setFilterStatuses(new Set());
    setFilterServer("");
    setFilterTable("");
  }
  function showFaceToast(message) {
    if (faceToastTimeoutRef.current) clearTimeout(faceToastTimeoutRef.current);
    setFaceToast(message);
    faceToastTimeoutRef.current = setTimeout(() => {
      setFaceToast(null);
      faceToastTimeoutRef.current = null;
    }, FACEID_TOAST_MS);
  }
  function applyFaceAction(action) {
    if (!action) return;
    if (action.type === "server") {
      setFilterStatuses(new Set());
      setFilterTable("");
      setFilterServer(action.serverName);
      showFaceToast(`Hello: ${action.serverName}`);
      return;
    }
    if (action.type === "clear") {
      setFilterServer("");
    }
  }
  function enqueueFaceAction(action) {
    const active = activeFaceActionRef.current;
    if (!active && faceActionQueue.length === 0) {
      applyFaceAction(action);
      setActiveFaceAction({
        ...action,
        startedAt: Date.now()
      });
      return;
    }
    const cancelActiveClear = active?.type === "clear" && action?.type === "server";
    if (cancelActiveClear) {
      setActiveFaceAction(null);
    }
    setFaceActionQueue(prev => {
      const activePrefix = active && !cancelActiveClear ? [{
        type: active.type,
        serverName: active.serverName || ""
      }] : [];
      const normalized = normalizeFaceActionQueue([...activePrefix, ...prev, action]);
      if (activePrefix.length && sameFaceAction(normalized[0], activePrefix[0])) {
        return normalized.slice(1);
      }
      return normalized;
    });
  }
  function getAffectedPrepStationIds(payload) {
    const rawIds = [payload?.sender_station_id, payload?.station_id, ...(Array.isArray(payload?.mirrored_station_ids) ? payload.mirrored_station_ids : [])];
    return Array.from(new Set(rawIds.map(value => Number(value)).filter(value => Number.isFinite(value) && value !== 1))).sort((a, b) => a - b);
  }
  useEffect(() => {
    if (!sublocationId) return;
    let active = true;
    (async () => {
      try {
        const stationsRes = await apiFetch(`/updates-reports/stations?sublocation_id=${encodeURIComponent(sublocationId)}`);
        const stations = stationsRes.ok ? await stationsRes.json() : [];
        if (!active) return;
        const nextStationMeta = {};
        let expoStationId = null;
        for (const row of Array.isArray(stations) ? stations : []) {
          const stationId = Number(row?.station_id);
          if (!Number.isFinite(stationId)) continue;
          nextStationMeta[String(stationId)] = {
            name: String(row?.station_name || row?.name || `Station ${stationId}`).trim(),
            role: String(row?.role || "").trim().toLowerCase()
          };
          if (nextStationMeta[String(stationId)].role === "expo" && (!Number.isFinite(expoStationId) || stationId < expoStationId)) {
            expoStationId = stationId;
          }
        }
        setStationMetaById(nextStationMeta);
        setExpoStationId(Number.isFinite(expoStationId) ? expoStationId : null);
        if (!Number.isFinite(expoStationId)) {
          setCourseLabels({});
          return;
        }
        const labelsRes = await apiFetch(`/settings/course_labels/${expoStationId}`);
        const labelsJson = labelsRes.ok ? await labelsRes.json() : {};
        if (!active) return;
        setCourseLabels(normalizeCourseLabels(labelsJson?.courseLabels));
      } catch (err) {
        console.error("Failed to load Queue Screen course labels/stations:", err);
        if (active) {
          setStationMetaById({});
          setCourseLabels({});
          setExpoStationId(null);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [sublocationId]);
  useEffect(() => {
    activeFaceActionRef.current = activeFaceAction;
  }, [activeFaceAction]);
  useEffect(() => {
    if (activeFaceAction || faceActionQueue.length === 0) return;
    const [nextAction, ...rest] = faceActionQueue;
    setFaceActionQueue(rest);
    applyFaceAction(nextAction);
    setActiveFaceAction({
      ...nextAction,
      startedAt: Date.now()
    });
  }, [activeFaceAction, faceActionQueue]);
  useEffect(() => {
    if (!activeFaceAction) {
      setFaceActionRemainingMs(FACEID_SWITCH_DELAY_MS);
      return undefined;
    }
    const startedAt = Number(activeFaceAction.startedAt) || Date.now();
    const updateRemaining = () => {
      setFaceActionRemainingMs(Math.max(0, FACEID_SWITCH_DELAY_MS - (Date.now() - startedAt)));
    };
    updateRemaining();
    const intervalId = setInterval(updateRemaining, 100);
    const timeoutId = setTimeout(() => {
      setActiveFaceAction(null);
    }, FACEID_SWITCH_DELAY_MS);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [activeFaceAction]);
  useEffect(() => () => {
    if (faceToastTimeoutRef.current) clearTimeout(faceToastTimeoutRef.current);
  }, []);

  /** ---------- helpers for station columns ---------- */
  function upsertStationCol(ticketKey, stationId, updater) {
    setTickets(prev => prev.map(t => {
      if (t.ticket_instance_id !== ticketKey) return t;
      const sid = String(stationId);
      const cur = t.stationCols?.[sid] || {
        messages: [],
        courses: [],
        courseStatus: {},
        ticketDone: false
      };
      const next = updater(cur) || cur;
      return {
        ...t,
        stationCols: {
          ...(t.stationCols || {}),
          [sid]: {
            messages: Array.isArray(next.messages) ? next.messages.slice(0, 10) : [],
            courses: Array.from(new Set((next.courses || []).map(Number))).sort((a, b) => a - b),
            courseStatus: {
              ...(cur.courseStatus || {}),
              ...(next.courseStatus || {})
            },
            ticketDone: !!next.ticketDone
          }
        }
      };
    }));
  }

  /** ---------- helpers: "Ran by" badge per course ---------- */
  function markCourseRanBy(ticketKey, courseNum, who) {
    setTickets(prev => prev.map(t => {
      if (t.ticket_instance_id !== ticketKey) return t;
      const courseRanBy = {
        ...(t.courseRanBy || {})
      };
      courseRanBy[Number(courseNum)] = String(who || "").trim();
      return {
        ...t,
        courseRanBy
      };
    }));
  }

  /** ---------- mark a specific course picked up (served) ---------- */
  function markCoursePickedUp(ticketKey, courseNum) {
    setTickets(prev => {
      return prev.map(t => {
        if (t.ticket_instance_id !== ticketKey) return t;
        const courseReady = {
          ...(t.courseReady || {})
        };
        const courseServed = {
          ...(t.courseServed || {})
        };

        // If it's already served, no-op
        if (courseServed[courseNum]) return t;
        delete courseReady[Number(courseNum)];
        courseServed[Number(courseNum)] = true;
        const totalCourses = Object.keys(t.courseTotals || {}).length;
        const servedCount = Object.keys(courseServed).filter(k => k in (t.courseTotals || {})).length;
        const fullyServed = totalCourses > 0 && servedCount >= totalCourses;
        return {
          ...t,
          courseReady,
          courseServed,
          readyToServe: Object.keys(courseReady).length > 0,
          servedAt: fullyServed ? t.servedAt || new Date().toISOString() : t.servedAt
        };
      });
    });

    // schedule removal after pulse if fully served
    setTimeout(() => {
      setTickets(prev => {
        const cur = prev.find(x => x.ticket_instance_id === ticketKey);
        if (!cur || !cur.servedAt) return prev;
        if (servedTimersRef.current.has(ticketKey)) {
          clearTimeout(servedTimersRef.current.get(ticketKey));
        }
        const to = setTimeout(() => {
          setTickets(p2 => p2.filter(x => x.ticket_instance_id !== ticketKey));
          servedTimersRef.current.delete(ticketKey);
        }, 5_000);
        servedTimersRef.current.set(ticketKey, to);
        return prev;
      });
    }, 0);
  }
  function hydrateQueueFromRows(rows = [], queueEvents = []) {
    const nextItemsIndex = new Map();
    const nextBumpedIndex = new Map();
    const nextAllItemsBumped = new Map();
    const nextTickets = [];
    const queueEventsByOrder = new Map();
    for (const evt of Array.isArray(queueEvents) ? queueEvents : []) {
      const orderKey = stableKeyFrom(evt);
      if (!orderKey) continue;
      const list = queueEventsByOrder.get(orderKey) || [];
      list.push(evt);
      queueEventsByOrder.set(orderKey, list);
    }
    const isDisplayRealItem = item => {
      const name = String(item?.Name || item?.name || "").trim();
      const category = String(item?.Category || item?.category || "").trim();
      const status = String(item?.status || "").toLowerCase();
      if (!name) return false;
      if (category === "Course Separator") return false;
      if (/^\*{3,}.*\*{3,}$/.test(name)) return false;
      if (name === "* MESSAGE *") return false;
      if (name === "------ FIRE ------") return false;
      if (Number(item?.TransType) === 2 || status === "voided") return false;
      return true;
    };
    for (const row of rows) {
      const key = stableKeyFrom(row);
      if (!key) continue;
      const rowStatus = String(row?.status || "").toLowerCase();
      if (rowStatus === "voided") {
        continue;
      }
      const ticket = typeof row?.ticket_json === "string" ? (() => {
        try {
          return JSON.parse(row.ticket_json);
        } catch {
          return null;
        }
      })() : row?.ticket_json ?? null;
      const normalizedHeaders = normalizeHeaderFields({
        ...ticket,
        ...row,
        user_info: row?.user_info ?? ticket?.UserInfo ?? ticket?.user_info ?? row?.UserInfo ?? null,
        guest_name: row?.guest_name ?? ticket?.guest_name ?? ticket?.guestName ?? row?.guestName ?? null
      });
      const rawItems = Array.isArray(ticket?.Item) ? ticket.Item : ticket?.Item ? [ticket.Item] : [];
      const items = rawItems.filter(isDisplayRealItem);
      if (items.length === 0) {
        continue;
      }
      const courseMap = new Map();
      const bumpMap = new Map();
      const courseReady = {};
      const stationCols = {};
      let bumpedCount = 0;
      for (const item of items) {
        const itemId = String(item?.ID || "").trim();
        if (!itemId) continue;
        const courseNum = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
        if (!courseMap.has(courseNum)) courseMap.set(courseNum, new Set());
        courseMap.get(courseNum).add(itemId);
        const itemStatus = String(item?.status || "").toLowerCase();
        if (itemStatus === "bumped" || item?.isBumped) {
          if (!bumpMap.has(courseNum)) bumpMap.set(courseNum, new Set());
          bumpMap.get(courseNum).add(itemId);
          bumpedCount += 1;
        }
        const displayBoxes = Array.isArray(item?.item_bump_display_boxes) ? item.item_bump_display_boxes : [];
        for (const box of displayBoxes) {
          const sid = Number(box?.station_id);
          if (!Number.isFinite(sid) || sid === 1) continue;
          const sidKey = String(sid);
          if (!stationCols[sidKey]) {
            stationCols[sidKey] = {
              messages: [],
              courses: [],
              courseStatus: {},
              ticketDone: false
            };
          }
          if (box?.received) {
            stationCols[sidKey].courses = Array.from(new Set([...(stationCols[sidKey].courses || []), courseNum])).sort((a, b) => a - b);
            if (box?.bumped && !stationCols[sidKey].courseStatus?.[courseNum]) {
              stationCols[sidKey].courseStatus[courseNum] = "working";
            }
          }
        }
      }
      for (const [courseNum, itemSet] of courseMap.entries()) {
        const itemCount = itemSet?.size || 0;
        const bumpedForCourse = (bumpMap.get(courseNum) || new Set()).size;
        if (itemCount > 0 && bumpedForCourse >= itemCount) {
          courseReady[Number(courseNum)] = true;
        }
      }
      const courseDisplayBoxes = row?.course_bump_display_boxes && typeof row.course_bump_display_boxes === "object" ? row.course_bump_display_boxes : {};
      for (const [courseKey, boxes] of Object.entries(courseDisplayBoxes)) {
        const courseNum = Number(courseKey);
        if (!Number.isFinite(courseNum)) continue;
        const expoBox = (Array.isArray(boxes) ? boxes : []).find(box => Number(box?.station_id) === 1);
        if (expoBox?.bumped) {
          courseReady[courseNum] = true;
        }
        for (const box of Array.isArray(boxes) ? boxes : []) {
          const sid = Number(box?.station_id);
          if (!Number.isFinite(sid) || sid === 1) continue;
          const sidKey = String(sid);
          if (!stationCols[sidKey]) {
            stationCols[sidKey] = {
              messages: [],
              courses: [],
              courseStatus: {},
              ticketDone: false
            };
          }
          if (box?.received) {
            stationCols[sidKey].courses = Array.from(new Set([...(stationCols[sidKey].courses || []), courseNum])).sort((a, b) => a - b);
            if (box?.bumped) {
              stationCols[sidKey].courseStatus[courseNum] = "done";
            } else if (!stationCols[sidKey].courseStatus?.[courseNum]) {
              stationCols[sidKey].courseStatus[courseNum] = "working";
            }
          }
        }
      }
      const ticketDisplayBoxes = Array.isArray(row?.ticket_bump_display_boxes) ? row.ticket_bump_display_boxes : [];
      for (const box of ticketDisplayBoxes) {
        const sid = Number(box?.station_id);
        if (!Number.isFinite(sid) || sid === 1) continue;
        const sidKey = String(sid);
        if (!stationCols[sidKey]) {
          stationCols[sidKey] = {
            messages: [],
            courses: [],
            courseStatus: {},
            ticketDone: false
          };
        }
        if (box?.received) {
          stationCols[sidKey].ticketDone = !!box?.bumped;
        }
      }
      const totalItems = Array.from(courseMap.values()).reduce((sum, set) => sum + (set?.size || 0), 0);
      const courseTotals = Object.fromEntries(Array.from(courseMap.entries()).sort((a, b) => Number(a[0]) - Number(b[0])).map(([courseNum, set]) => [courseNum, set.size]));
      nextItemsIndex.set(key, courseMap);
      nextBumpedIndex.set(key, bumpMap);
      if (String(row?.status || "").toLowerCase() === "bumped") {
        nextAllItemsBumped.set(key, true);
      }
      const hydratedTicket = {
        ticket_instance_id: key,
        raw_ticket_instance_id: row?.ticket_instance_id ?? null,
        order_id: row?.order_id ?? null,
        table_name: normalizedHeaders.table_name ?? null,
        server_name: normalizedHeaders.server_name ?? null,
        destination: normalizedHeaders.destination ?? null,
        user_info: normalizedHeaders.user_info ?? null,
        covers: ticket?.OrderMessages?.covers ?? null,
        createdAt: row?.first_received_at || row?.received_at || new Date().toISOString(),
        finishedAt: null,
        servedAt: null,
        stationsSeen: Object.keys(stationCols),
        ticketMessages: [],
        activityLog: [],
        bumpedCount,
        totalItems,
        courseTotals,
        stationCols,
        courseRanBy: {},
        courseReady,
        courseServed: {},
        anyCourseBumped: bumpedCount > 0,
        readyToServe: Object.keys(courseReady).length > 0
      };
      const runEvents = queueEventsByOrder.get(key) || [];
      if (runEvents.length > 0) {
        const courseServed = {
          ...(hydratedTicket.courseServed || {})
        };
        const courseRanBy = {
          ...(hydratedTicket.courseRanBy || {})
        };
        const nextCourseReady = {
          ...(hydratedTicket.courseReady || {})
        };
        let latestServedAt = hydratedTicket.servedAt;
        for (const evt of runEvents) {
          if (String(evt?.event_type || "").toLowerCase() !== "course_ran") continue;
          const courseNum = Number(evt?.course);
          if (!Number.isFinite(courseNum)) continue;
          courseServed[courseNum] = true;
          delete nextCourseReady[courseNum];
          const who = String(evt?.ran_by || "").trim();
          if (who) {
            courseRanBy[courseNum] = who;
          }
          const eventAt = evt?.event_at || evt?.created_at || null;
          if (eventAt && (!latestServedAt || new Date(eventAt) > new Date(latestServedAt))) {
            latestServedAt = eventAt;
          }
        }
        hydratedTicket.courseServed = courseServed;
        hydratedTicket.courseRanBy = courseRanBy;
        hydratedTicket.courseReady = nextCourseReady;
        hydratedTicket.readyToServe = Object.keys(nextCourseReady).length > 0;
        const totalCourses = Object.keys(hydratedTicket.courseTotals || {}).length;
        const servedCount = Object.keys(courseServed).filter(courseKey => courseKey in (hydratedTicket.courseTotals || {})).length;
        if (totalCourses > 0 && servedCount >= totalCourses) {
          hydratedTicket.servedAt = latestServedAt || new Date().toISOString();
        }
      }
      if (hydratedTicket.servedAt) {
        continue;
      }
      nextTickets.push(hydratedTicket);
    }
    itemsIndexRef.current = nextItemsIndex;
    bumpedIndexRef.current = nextBumpedIndex;
    allItemsBumpedRef.current = nextAllItemsBumped;
    setTickets(nextTickets);
  }

  /** ---------- ticket summary + totals ---------- */
  const ensureTicketSummary = payload => {
    setTickets(prev => {
      const key = findMatchingTicketKey(prev, payload);
      if (!key) return prev;
      const idx = prev.findIndex(t => t.ticket_instance_id === key);
      const now = new Date().toISOString();
      const stationId = payload.sender_station_id ?? payload.station_id ?? null;
      const normalizedHeaders = normalizeHeaderFields(payload);
      const rawCovers = payload.covers;
      const covers = rawCovers == null ? null : /^\s*\d+\s*$/.test(String(rawCovers)) ? String(rawCovers).trim() : /^covers:\s*(\d+)/i.exec(String(rawCovers))?.[1] ?? String(rawCovers).trim();
      const msgEntries = Array.isArray(payload.ticket_messages) ? payload.ticket_messages.map(m => String(m)) : payload.ticket_messages && typeof payload.ticket_messages === "object" ? Object.values(payload.ticket_messages).map(m => String(m)) : [];
      if (idx >= 0) {
        const cur = prev[idx];
        const merged = {
          ...cur,
          order_id: payload.order_id ?? cur.order_id ?? null,
          user_info: normalizedHeaders.user_info ?? cur.user_info ?? null,
          table_name: normalizedHeaders.table_name ?? cur.table_name ?? null,
          server_name: normalizedHeaders.server_name ?? cur.server_name ?? null,
          destination: normalizedHeaders.destination ?? cur.destination ?? null,
          covers: covers ?? cur.covers ?? null,
          createdAt: cur.createdAt ?? (payload.received_at || now),
          ticketMessages: (Array.isArray(cur.ticketMessages) ? cur.ticketMessages : []).concat(msgEntries).slice(-10),
          stationsSeen: Array.from(new Set([...(cur.stationsSeen || []), ...(stationId != null ? [String(stationId)] : [])])),
          bumpedCount: cur.bumpedCount ?? 0,
          totalItems: cur.totalItems ?? 0,
          courseTotals: cur.courseTotals ?? {},
          stationCols: cur.stationCols || {},
          courseRanBy: cur.courseRanBy || {},
          // <-- store "ran by"
          courseReady: cur.courseReady || {},
          courseServed: cur.courseServed || {},
          anyCourseBumped: !!cur.anyCourseBumped,
          readyToServe: !!cur.readyToServe
        };
        return [...prev.slice(0, idx), merged, ...prev.slice(idx + 1)];
      }
      return [...prev, {
        ticket_instance_id: key,
        order_id: payload.order_id ?? null,
        table_name: normalizedHeaders.table_name ?? null,
        server_name: normalizedHeaders.server_name ?? null,
        destination: normalizedHeaders.destination ?? null,
        user_info: normalizedHeaders.user_info ?? null,
        covers,
        createdAt: payload.received_at || now,
        finishedAt: null,
        servedAt: null,
        stationsSeen: stationId != null ? [String(stationId)] : [],
        ticketMessages: msgEntries.slice(-10),
        activityLog: [],
        bumpedCount: 0,
        totalItems: 0,
        courseTotals: {},
        stationCols: {},
        courseRanBy: {},
        // <-- init
        courseReady: {},
        courseServed: {},
        anyCourseBumped: false,
        readyToServe: false
      }];
    });
  };
  const recomputeTotalsFromIndex = ticketKey => {
    const courseMap = itemsIndexRef.current.get(ticketKey) || new Map();
    const courseTotals = {};
    let total = 0;
    Array.from(courseMap.entries()).sort((a, b) => Number(a[0]) - Number(b[0])).forEach(([c, set]) => {
      const n = set?.size ?? 0;
      courseTotals[Number(c)] = n;
      total += n;
    });
    setTickets(prev => prev.map(t => t.ticket_instance_id === ticketKey ? {
      ...t,
      totalItems: total,
      courseTotals
    } : t));
  };
  const recomputeBumpedTotals = ticketKey => {
    const bumpMap = bumpedIndexRef.current.get(ticketKey) || new Map();
    let bumped = 0;
    for (const set of bumpMap.values()) bumped += set.size || 0;
    setTickets(prev => prev.map(t => t.ticket_instance_id === ticketKey ? {
      ...t,
      bumpedCount: bumped
    } : t));
  };
  const courseList = (courseTotals = {}) => Object.entries(courseTotals).map(([c, n]) => ({
    c: Number(c),
    n: Number(n)
  })).sort((a, b) => a.c - b.c);
  function syncCourseReadyFromIndexes(ticketKey, courseNum) {
    const courseMap = itemsIndexRef.current.get(ticketKey) || new Map();
    const bumpMap = bumpedIndexRef.current.get(ticketKey) || new Map();
    const itemCount = (courseMap.get(courseNum) || new Set()).size;
    const bumpedCount = (bumpMap.get(courseNum) || new Set()).size;
    markCourseReady(ticketKey, courseNum, itemCount > 0 && bumpedCount >= itemCount);
  }

  // EXPO marks course ready => set per-course ready and ticket readyToServe if any course ready
  function markCourseReady(ticketKey, courseNum, ready = true) {
    setTickets(prev => prev.map(t => {
      if (t.ticket_instance_id !== ticketKey) return t;
      const courseReady = {
        ...(t.courseReady || {})
      };
      if (ready) courseReady[Number(courseNum)] = true;else delete courseReady[Number(courseNum)];
      const anyReady = Object.keys(courseReady).length > 0;
      return {
        ...t,
        courseReady,
        anyCourseBumped: true,
        readyToServe: anyReady
      };
    }));
  }

  /** Choose the first course that is READY and not already served/ran */
  function getNextRunnableCourse(t) {
    const ready = Object.keys(t.courseReady || {}).map(Number).sort((a, b) => a - b);
    for (const c of ready) {
      const alreadyServed = !!(t.courseServed && t.courseServed[c]);
      const alreadyRan = !!(t.courseRanBy && t.courseRanBy[c]);
      if (!alreadyServed && !alreadyRan) return c;
    }
    return null;
  }

  /** ---------- SOCKETS ---------- */
  useEffect(() => {
    if (!sublocationId) return undefined;
    const payload = {
      sublocationId: Number(sublocationId)
    };
    const joinRoom = () => socket.emit("queue-screen:join", payload);
    joinRoom();
    socket.on("connect", joinRoom);
    return () => {
      socket.off("connect", joinRoom);
      socket.emit("queue-screen:leave", payload);
    };
  }, [sublocationId]);
  useEffect(() => {
    const onConnect = () => {
      if (!expoStationId) return;
      (async () => {
        try {
          const [activeRes, bumpedRes, queueEventsRes] = await Promise.all([apiFetch(`/orders?station_id=${encodeURIComponent(expoStationId)}`), apiFetch(`/orders/bumped-today?station_id=${encodeURIComponent(expoStationId)}`), apiFetch(`/orders/queue-screen-events?sublocation_id=${encodeURIComponent(sublocationId)}`)]);
          const activeRows = activeRes.ok ? await activeRes.json() : [];
          const bumpedRows = bumpedRes.ok ? await bumpedRes.json() : [];
          const queueEvents = queueEventsRes.ok ? await queueEventsRes.json() : [];
          const mergedByOrder = new Map();
          for (const row of [...(Array.isArray(activeRows) ? activeRows : []), ...(Array.isArray(bumpedRows) ? bumpedRows : [])]) {
            const key = stableKeyFrom(row);
            if (!key) continue;
            mergedByOrder.set(key, row);
          }
          hydrateQueueFromRows(Array.from(mergedByOrder.values()), queueEvents);
        } catch (err) {
          console.error("Failed to hydrate Queue Screen from DB:", err);
        }
      })();
    };
    const onItemReceived = p => {
      const role = (p?.sender_role || "").toLowerCase();
      const tk = stableKeyFrom(p);
      const stationId = Number(p.sender_station_id ?? p.station_id);
      const itemId = String(p.item_id ?? "");
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (!itemId) return;
      if (role === "expo") {
        let courseNum = Number(p.course);
        if (!Number.isFinite(courseNum) || courseNum < 0) courseNum = 0;
        let courseMap = itemsIndexRef.current.get(tk);
        if (!courseMap) {
          courseMap = new Map();
          itemsIndexRef.current.set(tk, courseMap);
        }
        let set = courseMap.get(courseNum);
        if (!set) {
          set = new Set();
          courseMap.set(courseNum, set);
        }
        if (!set.has(itemId)) {
          set.add(itemId);
          recomputeTotalsFromIndex(tk);
        }
        if (allItemsBumpedRef.current.get(tk)) {
          let bumpMap = bumpedIndexRef.current.get(tk);
          if (!bumpMap) {
            bumpMap = new Map();
            bumpedIndexRef.current.set(tk, bumpMap);
          }
          let bset = bumpMap.get(courseNum);
          if (!bset) {
            bset = new Set();
            bumpMap.set(courseNum, bset);
          }
          if (!bset.has(itemId)) {
            bset.add(itemId);
            recomputeBumpedTotals(tk);
          }
        }
        return;
      }

      // non-expo
      if (!Number.isFinite(stationId) || stationId === 1) return;
      const courseMap = itemsIndexRef.current.get(tk);
      if (!courseMap) return;
      let foundCourse = null;
      for (const [c, setIds] of courseMap.entries()) {
        if (setIds.has(itemId)) {
          foundCourse = Number(c);
          break;
        }
      }
      if (foundCourse == null) return;
      upsertStationCol(tk, stationId, cur => ({
        messages: cur.messages,
        courses: [...cur.courses, foundCourse],
        courseStatus: cur.courseStatus,
        ticketDone: cur.ticketDone
      }));
    };
    const onOrderReceived = p => {
      const unifiedKey = stableKeyFrom(p);
      const stationId = Number(p.sender_station_id ?? p.station_id);
      if (!unifiedKey) return;
      const last = seenOrderReceivedRef.current.get(unifiedKey);
      if (within(5000, last)) return;
      seenOrderReceivedRef.current.set(unifiedKey, Date.now());
      ensureTicketSummary(p);
      setTickets(prev => prev.map(t => {
        if (t.ticket_instance_id !== unifiedKey) return t;
        const msg = `S${p.sender_station_id ?? "?"} received · ${new Date(p.received_at || Date.now()).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })}`;
        const log = [{
          ts: Date.now(),
          msg
        }, ...(t.activityLog || [])].slice(0, 10);
        return {
          ...t,
          activityLog: log
        };
      }));
      if (Number.isFinite(stationId) && stationId !== 1) {
        upsertStationCol(unifiedKey, stationId, cur => {
          const already = cur.messages.includes("Order received");
          return {
            messages: already ? cur.messages : ["Order received", ...cur.messages],
            courses: cur.courses,
            courseStatus: cur.courseStatus,
            ticketDone: cur.ticketDone
          };
        });
      }
    };
    const onBumpItem = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      const stationId = Number(p.sender_station_id ?? p.station_id);
      const itemId = String(p.item_id ?? "");
      const isBumped = !!p.isBumped;
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (!itemId) return;
      const ensureSet = (outer, key) => {
        let inner = outer.get(key);
        if (!inner) {
          inner = new Set();
          outer.set(key, inner);
        }
        return inner;
      };
      if (role === "expo" || stationId === 1) {
        let courseNum = Number(p.course);
        if (!Number.isFinite(courseNum) || courseNum < 0) courseNum = 0;
        let courseMap = itemsIndexRef.current.get(tk);
        if (!courseMap) {
          courseMap = new Map();
          itemsIndexRef.current.set(tk, courseMap);
        }
        const itemsSet = ensureSet(courseMap, courseNum);
        if (!itemsSet.has(itemId)) {
          itemsSet.add(itemId);
          recomputeTotalsFromIndex(tk);
        }
        let bumpMap = bumpedIndexRef.current.get(tk);
        if (!bumpMap) {
          bumpMap = new Map();
          bumpedIndexRef.current.set(tk, bumpMap);
        }
        const bumpedSet = ensureSet(bumpMap, courseNum);
        if (isBumped) bumpedSet.add(itemId);else bumpedSet.delete(itemId);
        recomputeBumpedTotals(tk);
        syncCourseReadyFromIndexes(tk, courseNum);
        if (isBumped) {
          setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
            ...t,
            anyCourseBumped: true
          } : t));
        }
        return;
      }
      const courseMap = itemsIndexRef.current.get(tk);
      let foundCourse = null;
      if (courseMap) {
        for (const [c, setIds] of courseMap.entries()) {
          if (setIds.has(itemId)) {
            foundCourse = Number(c);
            break;
          }
        }
      }
      if (foundCourse == null && Number.isFinite(Number(p.course))) {
        foundCourse = Number(p.course);
      }
      if (foundCourse == null) return;
      const affectedStationIds = getAffectedPrepStationIds(p);
      if (!affectedStationIds.length) return;
      for (const sid of affectedStationIds) {
        upsertStationCol(tk, sid, cur => {
          const nextStatus = {
            ...(cur.courseStatus || {})
          };
          if (isBumped) nextStatus[foundCourse] = "working";else delete nextStatus[foundCourse];
          return {
            messages: cur.messages,
            courses: [...cur.courses, foundCourse],
            courseStatus: nextStatus,
            ticketDone: cur.ticketDone
          };
        });
      }
      setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
        ...t,
        anyCourseBumped: true
      } : t));
    };
    const onBumpCourse = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      const stationId = Number(p.sender_station_id ?? p.station_id);
      let courseNum = Number(p.course);
      if (!Number.isFinite(courseNum)) courseNum = null;
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (role === "expo" || stationId === 1) {
        if (courseNum != null) {
          markCourseReady(tk, courseNum, true);
        }
        return;
      }
      const affectedStationIds = getAffectedPrepStationIds(p);
      if (!affectedStationIds.length) return;
      let targetCourses = [];
      if (courseNum != null) {
        targetCourses.push(courseNum);
      } else {
        setTickets(prev => {
          const t = prev.find(x => x.ticket_instance_id === tk);
          const working = [];
          for (const sid of affectedStationIds) {
            const col = t?.stationCols?.[String(sid)];
            const found = col?.courseStatus ? Object.entries(col.courseStatus).filter(([, v]) => v === "working").map(([k]) => Number(k)) : [];
            working.push(...found);
          }
          targetCourses = Array.from(new Set(working)).sort((a, b) => a - b);
          return prev;
        });
      }
      if (targetCourses.length === 0) return;
      for (const sid of affectedStationIds) {
        upsertStationCol(tk, sid, cur => {
          const nextStatus = {
            ...(cur.courseStatus || {})
          };
          for (const c of targetCourses) nextStatus[Number(c)] = "done";
          return {
            messages: cur.messages,
            courses: [...cur.courses, ...targetCourses],
            courseStatus: nextStatus,
            ticketDone: cur.ticketDone
          };
        });
      }
      setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
        ...t,
        anyCourseBumped: true
      } : t));
    };
    const onBumpTicket = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      const stationId = Number(p.sender_station_id ?? p.station_id);
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      const isExpo = role === "expo" || stationId === 1;
      if (isExpo) {
        const courseMap = itemsIndexRef.current.get(tk) || new Map();
        let bumpMap = bumpedIndexRef.current.get(tk);
        if (!bumpMap) {
          bumpMap = new Map();
          bumpedIndexRef.current.set(tk, bumpMap);
        }
        for (const [c, setIds] of courseMap.entries()) {
          let bset = bumpMap.get(c);
          if (!bset) {
            bset = new Set();
            bumpMap.set(c, bset);
          }
          for (const id of setIds) bset.add(id);
        }
        allItemsBumpedRef.current.set(tk, true);
        recomputeBumpedTotals(tk);
        setTickets(prev => prev.map(t => {
          if (t.ticket_instance_id !== tk) return t;
          const courseMapNow = itemsIndexRef.current.get(tk) || new Map();
          const courseReady = {};
          for (const c of courseMapNow.keys()) courseReady[Number(c)] = true;
          return {
            ...t,
            courseReady,
            anyCourseBumped: true,
            readyToServe: Object.keys(courseReady).length > 0
          };
        }));
      } else {
        const affectedStationIds = getAffectedPrepStationIds(p);
        for (const sid of affectedStationIds) {
          upsertStationCol(tk, sid, cur => ({
            messages: cur.messages,
            courses: cur.courses,
            courseStatus: cur.courseStatus,
            ticketDone: true
          }));
        }
        setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
          ...t,
          anyCourseBumped: true
        } : t));
      }
    };

    /** --- run-course listener (authoritative + idempotent) --- */
    const onRunCourse = p => {
      // Expect: { order_id, course, ran_by }
      const orderId = p?.order_id != null ? String(p.order_id) : null;
      const courseNum = Number(p?.course);
      const who = String(p?.ran_by || "").trim();
      if (!orderId || !Number.isFinite(courseNum) || !who) return;

      // ensure a summary exists to map by order_id
      ensureTicketSummary({
        order_id: orderId
      });

      // Apply: set Ran By only once, mark that course as picked up, and update ticket status
      setTickets(prev => prev.map(t => {
        if (String(t.order_id) !== orderId) return t;
        const courseRanBy = {
          ...(t.courseRanBy || {})
        };
        const courseReady = {
          ...(t.courseReady || {})
        };
        const courseServed = {
          ...(t.courseServed || {})
        };
        if (!courseRanBy[courseNum]) {
          courseRanBy[courseNum] = who;
        }

        // If not already picked up, pick it up now
        if (!courseServed[courseNum]) {
          delete courseReady[courseNum];
          courseServed[courseNum] = true;
        }
        const totalCourses = Object.keys(t.courseTotals || {}).length;
        const servedCount = Object.keys(courseServed).filter(k => k in (t.courseTotals || {})).length;
        const fullyServed = totalCourses > 0 && servedCount >= totalCourses;
        return {
          ...t,
          courseRanBy,
          courseReady,
          courseServed,
          readyToServe: Object.keys(courseReady).length > 0,
          servedAt: fullyServed ? t.servedAt || new Date().toISOString() : t.servedAt
        };
      }));

      // schedule removal after pulse if fully served
      setTimeout(() => {
        setTickets(prev => {
          const cur = prev.find(x => String(x.order_id) === orderId);
          if (!cur || !cur.servedAt) return prev;
          const ticketKey = cur.ticket_instance_id;
          if (servedTimersRef.current.has(ticketKey)) {
            clearTimeout(servedTimersRef.current.get(ticketKey));
          }
          const to = setTimeout(() => {
            setTickets(p2 => p2.filter(x => x.ticket_instance_id !== ticketKey));
            servedTimersRef.current.delete(ticketKey);
          }, 5_000);
          servedTimersRef.current.set(ticketKey, to);
          return prev;
        });
      }, 0);
    };
    const onFaceRecognized = p => {
      const nextServer = String(p?.server_name || "").trim();
      if (!nextServer) return;
      enqueueFaceAction({
        type: "server",
        serverName: nextServer
      });
    };
    const onFaceLost = () => {
      enqueueFaceAction({
        type: "clear"
      });
    };
    socket.on("connect", onConnect);
    socket.on("queue-screen:order-received", onOrderReceived);
    socket.on("queue-screen:item-received", onItemReceived);
    socket.on("queue-screen:bump-item", onBumpItem);
    socket.on("queue-screen:bump-course", onBumpCourse);
    socket.on("queue-screen:bump-ticket", onBumpTicket);
    socket.on("queue-screen:run-course", onRunCourse);
    socket.on("queue-screen:face-recognized", onFaceRecognized);
    socket.on("queue-screen:face-lost", onFaceLost);
    if (socket.connected) onConnect();
    return () => {
      socket.off("connect", onConnect);
      socket.off("queue-screen:order-received", onOrderReceived);
      socket.off("queue-screen:item-received", onItemReceived);
      socket.off("queue-screen:bump-item", onBumpItem);
      socket.off("queue-screen:bump-course", onBumpCourse);
      socket.off("queue-screen:bump-ticket", onBumpTicket);
      socket.off("queue-screen:run-course", onRunCourse);
      socket.off("queue-screen:face-recognized", onFaceRecognized);
      socket.off("queue-screen:face-lost", onFaceLost);
      for (const to of servedTimersRef.current.values()) clearTimeout(to);
      servedTimersRef.current.clear();
    };
  }, [expoStationId, sublocationId]);

  /** -------- Double-tap: emit run-course (once) and mark picked up -------- */
  const lastTapRef = useRef(new Map());
  function handleCardClick(t) {
    const now = Date.now();
    const last = lastTapRef.current.get(t.ticket_instance_id) || 0;
    lastTapRef.current.set(t.ticket_instance_id, now);
    if (now - last < 300) {
      if (t.readyToServe) {
        const nextCourse = getNextRunnableCourse(t); // <-- updated logic
        if (nextCourse != null) {
          const eventId = typeof window !== "undefined" && window.crypto?.randomUUID?.() || `queue-run-${String(t.order_id ?? t.ticket_instance_id ?? "unknown")}-${nextCourse}-${Date.now()}`;
          socket.emit("run-course", {
            order_id: t.order_id ?? null,
            ticket_instance_id: t.raw_ticket_instance_id ?? t.ticket_instance_id ?? null,
            course: nextCourse,
            // 0-based
            course_label: courseLabelFor(nextCourse, courseLabels),
            ran_by: "QueueScreen",
            sender_screen_id: "queue_screen",
            sender_station_id: expoStationId ?? 1,
            sender_role: "expo",
            sublocation_id: sublocationId ? Number(sublocationId) : null,
            table_name: t.table_name ?? null,
            server_name: t.server_name ?? null,
            user_info: t.user_info ?? null,
            event_id: eventId,
            sentAt: new Date().toISOString()
          });
          // optimistic: show "Ran by" and mark this specific course picked up
          markCourseRanBy(t.ticket_instance_id, nextCourse, "QueueScreen");
          markCoursePickedUp(t.ticket_instance_id, nextCourse);
        }
      }
    }
  }

  /** -------- Filters -> Sort -------- */
  const filteredThenSorted = useMemo(() => {
    let arr = tickets.filter(t => {
      const st = statusOfTicket(t);
      if (filterStatuses.size && !filterStatuses.has(st)) return false;
      if (filterServer && t.server_name !== filterServer) return false;
      if (filterTable && t.table_name !== filterTable) return false;
      return true;
    });
    switch (sortKey) {
      case "table":
        arr.sort((a, b) => String(a.table_name || "").localeCompare(String(b.table_name || "")));
        break;
      case "server":
        arr.sort((a, b) => String(a.server_name || "").localeCompare(String(b.server_name || "")));
        break;
      case "time":
      default:
        arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
    }
    return arr;
  }, [tickets, sortKey, filterStatuses, filterServer, filterTable]);
  const faceActionProgressPct = activeFaceAction ? Math.max(0, Math.min(100, faceActionRemainingMs / FACEID_SWITCH_DELAY_MS * 100)) : 0;
  const faceActionLabel = activeFaceAction?.type === "server" ? `Showing ${activeFaceAction.serverName}` : activeFaceAction?.type === "clear" ? "Returning to All Servers" : "";
  const queuedFaceActionLabel = faceActionQueue.map(action => action?.type === "server" ? action.serverName : "All Servers").filter(Boolean).join(" -> ");
  const showFaceQueuePanel = !!activeFaceAction;
  return <div className="flex flex-col min-h-screen font-sans bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
            {/* local CSS for slow pulse */}
            <style>{`
        @keyframes pulse-slow { 0%,100% { opacity: 1 } 50% { opacity: 0.55 } }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
      `}</style>

            <style>{`
        @keyframes faceid-banner-in {
          0% { opacity: 0; transform: translate3d(20px, -18px, 0) rotate(8deg) scale(0.92); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) rotate(8deg) scale(1); }
        }
        @keyframes faceid-banner-out {
          0% { opacity: 1; transform: translate3d(0, 0, 0) rotate(8deg) scale(1); }
          100% { opacity: 0; transform: translate3d(28px, -18px, 0) rotate(11deg) scale(0.985); }
        }
        .faceid-banner {
          animation:
            faceid-banner-in 140ms cubic-bezier(0.16, 1, 0.3, 1),
            faceid-banner-out 2000ms ease-out 220ms forwards;
          transform: rotate(8deg);
          transform-origin: top right;
        }
      `}</style>

            {faceToast && <div className="faceid-banner fixed right-4 top-4 z-50 max-w-[34rem] rounded-[2rem] border-2 border-emerald-300 bg-emerald-50/97 px-8 py-6 text-emerald-950 shadow-[0_20px_60px_rgba(0,0,0,0.28)] backdrop-blur dark:border-emerald-600 dark:bg-emerald-900/97 dark:text-emerald-50">
                    <div className="text-[2.4rem] font-black leading-none tracking-tight">{String(faceToast).replace(/^Hello:\s*/, "Hello ") + "!"}</div>
                </div>}
            <TopBar onToggleDarkMode={toggleDarkMode} isDark={isDark} onOpenSettings={() => setShowSettings(true)} />

            {/* Controls: Sort + Filter */}
            <div className="px-4 py-3 flex flex-wrap gap-4 items-center sticky top-0 z-10 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur">
                {/* Sort */}
                <div className="flex items-center gap-2">
                    <label className="text-sm opacity-70">Sort By</label>
                    <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
                        <button onClick={() => setSortKey("table")} className={`px-3 py-1 text-sm ${sortKey === "table" ? "bg-gray-900 text-white" : "bg-white dark:bg-gray-800"}`}>Table</button>
                        <button onClick={() => setSortKey("server")} className={`px-3 py-1 text-sm ${sortKey === "server" ? "bg-gray-900 text-white" : "bg-white dark:bg-gray-800"}`}>Server</button>
                        <button onClick={() => setSortKey("time")} className={`px-3 py-1 text-sm ${sortKey === "time" ? "bg-gray-900 text-white" : "bg-white dark:bg-gray-800"}`}>Ticket Time</button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm opacity-70">Filter By</span>

                    {/* Status chips (multi-select) */}
                    <div className="flex gap-2">
                        {["Received", "In Progress", "Ready To Serve", "Finished"].map(s => {
            const active = filterStatuses.has(s);
            return <button key={s} onClick={() => toggleStatus(s)} className={`px-2 py-1 text-xs rounded border ${active ? "bg-yellow-400 text-black border-yellow-500" : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"}`}>
                                    {s}
                                </button>;
          })}
                    </div>

                    {/* Server dropdown */}
                    <select value={filterServer} onChange={e => setFilterServer(e.target.value)} className="text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <option value="">All Servers</option>
                        {servers.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>

                    {/* Table dropdown */}
                    <select value={filterTable} onChange={e => setFilterTable(e.target.value)} className="text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <option value="">All Tables</option>
                        {tables.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {/* Clear */}
                    <button onClick={clearFilters} className="text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">Clear</button>
                </div>
            </div>

            {showFaceQueuePanel && <div className="px-4 pb-3">
                    <div className="rounded-2xl border border-sky-200 bg-white/95 px-4 py-3 shadow-sm dark:border-sky-800 dark:bg-slate-900/95">
                        <div className="mb-2 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-sky-900 dark:text-sky-100">{faceActionLabel}</div>
                                <div className="text-xs opacity-70">
                                    {Math.ceil(faceActionRemainingMs / 1000)}s remaining
                                    {faceActionQueue.length > 0 ? ` • ${faceActionQueue.length} queued` : ""}
                                </div>
                                {queuedFaceActionLabel && <div className="mt-1 text-xs opacity-70">
                                        Up Next: {queuedFaceActionLabel}
                                    </div>}
                            </div>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950">
                            <div className="h-full rounded-full bg-sky-500 transition-[width] duration-100 ease-linear dark:bg-sky-400" style={{
                width: `${faceActionProgressPct}%`
              }} />
                        </div>
                    </div>
                </div>}

            {/* Cards grid */}
            <main className="flex-1 px-3 pb-6">
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {filteredThenSorted.map(t => {
          const st = statusOfTicket(t);
          const color = statusColors(st, isDark);
          const courses = courseList(t.courseTotals);
          const stationCols = t.stationCols || {};
          const nonExpoIds = Object.keys(stationCols).filter(sid => sid !== "1");
          const nCols = nonExpoIds.length;
          const courseMap = itemsIndexRef.current.get(t.ticket_instance_id) || new Map();
          const bumpMap = bumpedIndexRef.current.get(t.ticket_instance_id) || new Map();
          const courseReady = t.courseReady || {};
          const courseServed = t.courseServed || {};
          const courseRanBy = t.courseRanBy || {};

          // UPDATED: show the actual next runnable course (skips already-ran/served)
          const nextReady = getNextRunnableCourse(t);
          return <div key={t.ticket_instance_id} className={`mt-5 rounded-xl border shadow-sm hover:shadow-md transition-all duration-150 ${color} select-none`} style={{
            minHeight: 200
          }} onClick={() => handleCardClick(t)} role="group">
                                <div className="p-3 space-y-2 leading-snug">
                                    {/* header */}
                                    <div className="flex items-center justify-between text-xs opacity-70">
                                        <span><strong>Ticket #:</strong> {t.order_id}</span>
                                        <span className={sortKey === "time" ? "text-yellow-500 font-semibold" : ""}>
                                            <strong>Received:</strong>{" "}
                                            {new Date(t.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                                        </span>
                                    </div>
                                    <div className="text-sm"><strong>Guest:</strong> {t.user_info || "—"}</div>
                                    <div className={`text-sm ${sortKey === "table" ? "text-yellow-500 font-semibold" : ""}`}><strong>Table:</strong> {t.table_name || "—"}</div>
                                    <div className={`text-sm ${sortKey === "server" ? "text-yellow-500 font-semibold" : ""}`}><strong>Server:</strong> {t.server_name || "—"}</div>
                                    {t.covers && <div className="text-sm"><strong>Covers:</strong> {t.covers}</div>}

                                    {/* Dynamic station strip */}
                                    {nCols > 0 && <div className="mt-2 rounded-md border border-black/10 dark:border-white/10 bg-white/60 dark:bg-gray-800/50 p-2">
                                            <div className="grid gap-2" style={{
                  gridTemplateColumns: `repeat(${nCols}, minmax(0,1fr))`
                }}>
                                                {nonExpoIds.map(sid => {
                    const col = stationCols[sid] || {
                      messages: [],
                      courses: [],
                      courseStatus: {},
                      ticketDone: false
                    };
                    const label = stationMetaById?.[sid]?.name || `Station ${sid}`;
                    return <div key={`col-${t.ticket_instance_id}-${sid}`} className="text-[12px]">
                                                            <div className="font-semibold opacity-80 mb-1">{label}</div>

                                                            {col.ticketDone && <div className="mb-1 inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-emerald-500/30 border border-emerald-700/40 text-emerald-900 dark:text-white">
                                                                    Finished
                                                                </div>}

                                                            {col.messages?.length > 0 && <div className="flex flex-col gap-1">
                                                                    {col.messages.slice(0, 3).map((m, i) => <div key={`m-${i}`} className="px-2 py-0.5 rounded bg-emerald-600/15 border border-emerald-700/30 dark:bg-emerald-900/20">
                                                                            {m}
                                                                        </div>)}
                                                                </div>}
                                                            {col.courses?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">
                                                                    {col.courses.map(c => {
                          const stc = col.courseStatus?.[c];
                          const isWorking = stc === "working";
                          const isDone = stc === "done";
                          return <span key={`c-${c}`} className={`px-2 py-0.5 rounded border text-[11px] ${isDone ? "bg-slate-500/15 border-slate-500/30 text-slate-700 dark:text-slate-300 line-through opacity-60" : isWorking ? "bg-yellow-400/40 border-yellow-600/40 text-yellow-900" : "bg-sky-500/10 border-sky-600/30"}`}>
                                                                                {courseLabelFor(c, courseLabels)}
                                                                            </span>;
                        })}
                                                                </div>}
                                                            {!col.messages?.length && !col.courses?.length && !col.ticketDone && <div className="opacity-50">—</div>}
                                                        </div>;
                  })}
                                            </div>
                                        </div>}

                                    {/* Items cooked X / Y */}
                                    <div className="text-sm"><strong>Items Cooked:</strong> {t.bumpedCount ?? 0} / {t.totalItems ?? 0}</div>

                                    {/* Course bars + "Ran by" badge */}
                                    {courses.length > 0 && <ul className="mt-2 space-y-1 text-[12px]">
                                            {courses.map(({
                  c
                }) => {
                  const ids = Array.from(courseMap.get(c) || []).sort();
                  const n = ids.length;
                  const bumpedSet = bumpMap.get(c) || new Set();
                  const isReady = !!courseReady[c] || n > 0 && bumpedSet.size >= n;
                  const isPickedUp = !!courseServed[c];
                  const ranBy = courseRanBy[c]; // who ran this course

                  return <li key={`course-${t.ticket_instance_id}-${c}`} className="rounded bg-white/70 dark:bg-gray-800/60 px-2 py-1">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium">{courseLabelFor(c, courseLabels)}</span>
                                                                {isReady && !isPickedUp && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-emerald-500/20 border border-emerald-600/40 text-emerald-900 dark:text-white">
                                                                        Ready for pickup
                                                                    </span>}
                                                                {isPickedUp && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-blue-500/20 border border-blue-600/40 text-blue-900 dark:text-white">
                                                                        Picked up
                                                                    </span>}
                                                                {ranBy && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-indigo-500/20 border border-indigo-600/40 text-indigo-900 dark:text-white">
                                                                        Ran by: {ranBy}
                                                                    </span>}
                                                            </div>
                                                            <span className="text-[11px] opacity-70">{n} item{n === 1 ? "" : "s"}</span>
                                                        </div>
                                                        <div className="mt-2 flex gap-1 flex-wrap">
                                                            {ids.map(id => {
                        const isBumped = bumpedSet.has(id);
                        return <div key={`slot-${c}-${id}`} className={`relative w-4 h-4 rounded-sm border border-black/20 dark:border-white/20 bg-white/70 dark:bg-gray-900/60 ${isBumped ? "opacity-60" : ""}`} title={`Course ${c + 1} • ${isBumped ? "Bumped" : "Not bumped"}`}>
                                                                        {isBumped && <>
                                                                                <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                                                                                <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 -rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                                                                            </>}
                                                                    </div>;
                      })}
                                                        </div>
                                                    </li>;
                })}
                                        </ul>}

                                    {/* optional small messages row */}
                                    {Array.isArray(t.ticketMessages) && t.ticketMessages.length > 0 && <div className="text-[11px] opacity-75">
                                            <strong>Msgs:</strong> {t.ticketMessages.slice(-2).join(" · ")}
                                        </div>}
                                </div>

                                <div className="grid grid-cols-2 text-center items-stretch border-t border-black/5 dark:border-white/10">
                                    <div className="px-2 py-2">
                                        <div className="text-xs font-semibold uppercase tracking-wide opacity-70">Status</div>
                                        <div className="text-sm font-medium">{st}</div>
                                    </div>
                                    <div className="px-2 py-2">
                                        <div className="text-xs font-semibold uppercase tracking-wide opacity-70">Actions</div>
                                        <div className="pt-1">
                                            <span className="text-[11px] opacity-70">
                                                {t.readyToServe ? `Double-tap to serve ${typeof nextReady === "number" ? courseLabelFor(nextReady, courseLabels) : "course"}` : "Kitchen preparing…"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>;
        })}
                </div>
            </main>

            {showSettings && <SettingsModal onClose={() => setShowSettings(false)} stationId={null} />}
        </div>;
}
